import { logger } from '@mono-farmart-web/logger';
/**
 * The function `GetRequest` sends a GET request to a specified URI with provided payload and handles
 * authentication, language settings, and error responses.
 * @param {getRequestQueryParameter} props - The `props` parameter in the `GetRequest` function is an
 * object with two properties:
 * @returns The GetRequest function returns a Promise that resolves to a UnifiedResponse object. The
 * UnifiedResponse object contains two properties: "data" which holds the data fetched from the API
 * response, and "response" which holds the entire API response object.
 */
import getConfigByKeys from '../../config/config';
import { configKeys } from '../../config/types';
import handleHttpError from '../api/httpErrorMapping';
import HttpStatusCode from '@mono-farmart-web/api-endpoints/types/httpStatusCodesEnums.type';
import type UnifiedResponse from '@mono-farmart-web/api-endpoints/types/unifiedResponse.type';

import { getHeaders } from './fetchHeaders';

const { API_URL } = getConfigByKeys([configKeys.API_URL]);

interface getRequestQueryParameter {
	payload: any;
	uri?: string;
	customUri?: string;
	signal?: AbortSignal; // Add signal parameter
}

export default async function GetRequest(
	props: getRequestQueryParameter,
): Promise<UnifiedResponse> {
	const { payload, uri, customUri, signal } = props;
	const headers = getHeaders();

	const response = await logger.fetch(
		`${API_URL}${customUri ? customUri : uri}?${new URLSearchParams(payload)}`,
		{
			method: 'GET',
			headers: headers,
			signal,
		},
		API_URL,
	);
	if (response.status !== HttpStatusCode.Ok) {
		const error: any = handleHttpError(response.status); // keep your custom error message
		error.code = response.status;

		try {
			const responseBody = await response.json();
			error.response = responseBody?.data ?? responseBody;
			// The whole envelope as well, the way postRequest already returns it. `response`
			// above keeps only the inner `data`, so the API's own `message` was unreachable to
			// callers and every 4xx read as a generic failure.
			error.data = responseBody;
		} catch (e) {
			error.response = null; // fallback if JSON can't be parsed
			error.data = null;
		}

		throw error;
	}

	const data = await response.json();
	return { data, response };
}
